import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface Ctx {
  theme: Theme;
  set: (t: Theme) => void;
}

const ThemeCtx = createContext<Ctx>({ theme: "system", set: () => undefined });

const STORAGE_KEY = "piggy.theme";

function apply(theme: Theme) {
  const isDark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", isDark);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? "system";
  });

  useEffect(() => {
    apply(theme);
    if (theme === "system") {
      const m = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => apply("system");
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    }
  }, [theme]);

  function set(t: Theme) {
    setTheme(t);
    localStorage.setItem(STORAGE_KEY, t);
  }

  return <ThemeCtx.Provider value={{ theme, set }}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): Ctx {
  return useContext(ThemeCtx);
}

export function ThemeToggle() {
  const { theme, set } = useTheme();
  const next: Record<Theme, Theme> = { light: "dark", dark: "system", system: "light" };
  const icon = theme === "dark" ? "🌙" : theme === "light" ? "☀" : "💻";
  return (
    <button
      onClick={() => set(next[theme])}
      title={`Theme: ${theme} (click to cycle)`}
      className="rounded-md px-2 py-1 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
    >
      {icon}
    </button>
  );
}
